import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { ESA_MEMBERSHIP_LOW_CREDIT } from "@outschool/business-rules";
import { faArrowUpRightFromSquare, faInfoCircle, faX } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { transactionsPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Container } from "@outschool/ui-legacy-component-library";
import { useNavigation } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import React from "react";

import { useEsaMembershipCreditBalances } from "../../hooks/useEsaMembershipCreditBalance";
import { useUpcomingChargesPerLearner } from "../nav/EsaMembershipNavItem";

const COOKIE_HIDE_LOW_ESA_BANNER = "hide_low_esa_banner";
const BANNER_HIDE_DAYS = 3;

export const LowEsaCreditBanner = () => {
  const { t } = useTranslation(
    "client\\components\\esaMemberships\\LowEsaCreditBanner",
  );
  const navigate = useNavigation();
  const [close, setClose] = React.useState<boolean>(false);
  const theme = useTheme();

  const { loading, hasEsaMembershipCreditBalances, creditBalances } =
    useEsaMembershipCreditBalances();

  const [upcomingChargeFrom, upcomingChargeTo] = React.useMemo(() => {
    return [dayjs().toDate(), dayjs().add(1, "week").endOf("week").toDate()];
  }, []);

  const { loading: upcomingChargesLoading, esaUpcomingChargesPerLearner } =
    useUpcomingChargesPerLearner(upcomingChargeFrom, upcomingChargeTo);

  const hideBanner = Cookies.get(COOKIE_HIDE_LOW_ESA_BANNER) === "true";

  const setHideLowEsaBanner = React.useCallback(() => {
    Cookies.set(COOKIE_HIDE_LOW_ESA_BANNER, "true", {
      expires: BANNER_HIDE_DAYS,
    });
  }, []);

  if (loading || upcomingChargesLoading || !hasEsaMembershipCreditBalances) {
    return null;
  }

  if (hideBanner) {
    return null;
  }

  if (close) {
    return null;
  }

  const lowCreditBalances = creditBalances.filter(balance => {
    return (
      balance.availableCredits <= ESA_MEMBERSHIP_LOW_CREDIT ||
      (esaUpcomingChargesPerLearner?.[balance.learnerUid] &&
        esaUpcomingChargesPerLearner[balance.learnerUid].priceCredits >
          balance.availableCredits)
    );
  });

  if (lowCreditBalances.length === 0) {
    return null;
  }

  const lowCreditBalancesByProgram: [string, string[]][] = Object.entries(
    lowCreditBalances.reduce((acc: Record<string, string[]>, balance) => {
      if (!balance.programName || !balance.name) {
        return acc;
      }
      if (acc[balance.programName]) {
        acc[balance.programName].push(balance.name);
      } else {
        acc[balance.programName] = [balance.name];
      }
      return acc;
    }, {}),
  );

  const formatName = (names: string[]) => {
    if (names.length === 2) {
      return t("{{firstLearnerName}} and {{secondLearnerName}}", {
        firstLearnerName: names[0],
        secondLearnerName: names[1],
      });
    }

    if (names.length > 2) {
      const lastName = names.pop();
      return t("{{learnerNames}} and {{lastLearnerName}}", {
        learnerNames: names.join(", "),
        lastLearnerName: lastName,
      });
    }

    return "";
  };

  return (
    <>
      {lowCreditBalancesByProgram.map(([programName, learnerNames], idx) => (
        <Container
          size="medium"
          sx={{ marginTop: "2.4rem", width: "100%" }}
          key={idx}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.info[50],
              color: theme.palette.info[700],
              border: `2px solid ${theme.palette.info[500]}`,
              borderRadius: "0.8rem",
              paddingY: "0.6rem",
              paddingX: "1.6rem",
              marginBottom: "0.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 20,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flex: 2,
                }}
              >
                <Box>
                  <Icon
                    icon={faInfoCircle}
                    sx={{
                      color: theme.palette.info[500],
                      marginRight: "0.5em",
                    }}
                  />
                </Box>
                <Box>
                  <Box>
                    <Typography variant="h6">
                      {learnerNames.length > 1
                        ? t(
                            "{{learnerName}} are running low on {{programName}} credits.",
                            {
                              learnerName: formatName(learnerNames),
                              programName: programName,
                            },
                          )
                        : t(
                            "{{learnerName}} is running low on {{programName}} credits.",
                            {
                              learnerName: learnerNames[0],
                              programName: programName,
                            },
                          )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1">
                      {t("If you run out of credits, we will charge your")}
                      &nbsp;
                      <u>{t("saved payment method")}</u>&nbsp;
                      {t(
                        "(if available) to keep classes going without disruption. To continue spending your {{programName}} funds, add more credits.",
                        {
                          programName,
                        },
                      )}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "none",
                      [theme.breakpoints.down("sm")]: {
                        display: "block",
                        marginTop: "0.5rem",
                      },
                    }}
                  >
                    <TrackedButton
                      variant="link"
                      trackingName="esa_membership_low_credit_banner_add"
                      onClick={() => {
                        navigate(transactionsPath());
                      }}
                    >
                      {t("Add Credits")}{" "}
                      <Icon
                        icon={faArrowUpRightFromSquare}
                        sx={{ marginLeft: "0.5rem" }}
                      />
                    </TrackedButton>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "block",
                  [theme.breakpoints.down("sm")]: {
                    display: "none",
                  },
                }}
              >
                <TrackedButton
                  variant="link"
                  trackingName="esa_membership_low_credit_banner_add"
                  onClick={() => {
                    navigate(transactionsPath());
                  }}
                >
                  {t("Add Credits")}{" "}
                  <Icon
                    icon={faArrowUpRightFromSquare}
                    sx={{ marginLeft: "0.5rem" }}
                  />
                </TrackedButton>
              </Box>
              <Box>
                <TrackedButton
                  variant="link"
                  trackingName="esa_membership_low_credit_banner_close"
                  onClick={() => {
                    setHideLowEsaBanner();
                    setClose(true);
                  }}
                >
                  <Icon icon={faX} />
                </TrackedButton>
              </Box>
            </Box>
          </Box>
        </Container>
      ))}
    </>
  );
};
